import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import * as yup from 'yup';
import AppliedApplicationTemplateContainer from '../../../state/containers/AppliedApplicationTemplateContainer';
import ApplicationContainer from '../../../state/containers/ApplicationContainer';
import TemplateFormFieldValueModel from '../../../models/templating/api/TemplateFormFieldValueModel';
import { FormikProps, withFormik } from 'formik';
import Loader from '../../../components/general/Loader';
import AppliedApplicationTemplateModel from '../../../models/templating/api/AppliedApplicationTemplateModel';
import PanelHeaderContainer from '../../../components/structure/Panels/PanelHeaderContainer';
import Button from '../../../components/general/Button';
import CustomScrollbars from '../../../components/structure/CustomScrollbars';
import TemplateFormSectionModel from '../../../models/templating/api/TemplateFormSectionModel';
import { color_text_default, color_colors_growth, color_colors_growth_background, color_text_link } from '../../../constants/colors';
import TemplateReviewSection from './TemplateReviewSection';
import PanelContainer from '../../../components/structure/Panels/PanelContainer';
import TemplateConfigurationModel from '../../../models/templating/api/TemplateConfigurationModel';
import ApplicationEnvironmentsContainer from '../../../state/containers/ApplicationEnvironmentsContainer';
import TemplateSaveChangesModal from '../../../components/templating/TemplateSaveChangesModal';
import { resolveConditionalDefaultsForSubmit } from '../../../utils/conditionalDefaultUtils';

const completeIcon = require('../../../content/images/templates-icons/complete-thin.svg');
const incompleteIcon = require('../../../content/images/templates-icons/incomplete.svg');
const generateIcon = require('../../../content/images/templates-icons/generate-content.svg');
const processingIcon = require('../../../content/images/templates-icons/processing-logo.svg');
const successfulSyncIcon = require('../../../content/images/templates-icons/successful-sync.svg');
const errorIcon = require('../../../content/images/alert-circle.svg');

interface AppliedAppTemplatesManagerProps {
    applicationId: string
    appContainer: ApplicationContainer
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    incompleteSections: TemplateFormSectionModel[]
    onPrevious: () => void
    onSectionSelect?: (section: TemplateFormSectionModel) => void
    history: any
    appliedAppTemplateId?: string
    appliedAppTemplate?: AppliedApplicationTemplateModel
    appliedAppValues?: TemplateFormFieldValueModel[]
    appliedTemplates?: AppliedApplicationTemplateModel[]
    templateConfiguration?: TemplateConfigurationModel
    applicationEnvironmentsContainer: ApplicationEnvironmentsContainer
    fieldValidationErrors?: Record<string, boolean>
}

interface AppliedAppTemplatesManagerFormData {
    templateFormValues?: TemplateFormFieldValueModel[]
    appliedTemplates?: AppliedApplicationTemplateModel[]
    isSuccess: boolean
    modifiedFields?: TemplateFormFieldValueModel[]
}

const InnerForm: React.FC<AppliedAppTemplatesManagerProps & FormikProps<AppliedAppTemplatesManagerFormData>> = (props) => {
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
    const [modifiedFields, setModifiedFields] = useState<TemplateFormFieldValueModel[]>([]);

    useEffect(() => {
        props.applicationEnvironmentsContainer.loadEnvironments(props.applicationId);
    }, [props.applicationId])

    useEffect(() => {
        props.setFieldValue("templateFormValues", props.appliedAppValues);
        if (isFormSubmitted) {
            setIsFormSubmitted(false);
        }
    }, [props.appliedAppValues]);

    const handleFormSubmit = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        const allFields = props.appliedAppTemplate?.templateForm?.templateFormSections?.flatMap(s => s.templateFormFields ?? []) ?? [];
        const resolved = resolveConditionalDefaultsForSubmit(allFields, props.values.templateFormValues ?? []);
        resolved.forEach((v, index) => {
            props.setFieldValue(`templateFormValues.${index}.value`, v.value ?? null);
        });
        // Don't call props.handleSubmit() here - let the modal handle it
    };

    const handleFinishAction = () => {
        // For the review form, always show the modal since this is the final confirmation
        setShowSaveChangesModal(true);
    };

    const performFinish = async (modifiedFieldsOnly?: TemplateFormFieldValueModel[]) => {
        // Resolve conditional defaults for fields the user never touched before submitting
        const allFields = props.appliedAppTemplate?.templateForm?.templateFormSections?.flatMap(s => s.templateFormFields ?? []) ?? [];
        const resolved = resolveConditionalDefaultsForSubmit(allFields, props.values.templateFormValues ?? []);
        for (const [index, v] of resolved.entries()) {
            await props.setFieldValue(`templateFormValues.${index}.value`, v.value ?? null);
        }

        // Store modified fields in form values for use in submission
        if (modifiedFieldsOnly && modifiedFieldsOnly.length > 0) {
            await props.setFieldValue('modifiedFields', modifiedFieldsOnly);
        }

        setIsFormSubmitted(true);
        props.handleSubmit();
    }; const submitForm = () => {
        handleFinishAction();
    };

    const closeSaveChangesModal = () => {
        setShowSaveChangesModal(false);
    };

    const goBack = () => {
        props.history.goBack();
    };

    const incompleteSections = props.incompleteSections;
    const hasValidationErrors = props.fieldValidationErrors ? Object.values(props.fieldValidationErrors).some(hasError => hasError) : false;

    // Get sections with validation errors for user-friendly error messages
    const getSectionsWithValidationErrors = (): TemplateFormSectionModel[] => {
        if (!props.fieldValidationErrors || !props.appliedAppTemplate?.templateForm?.templateFormSections) {
            return [];
        }

        const sectionsWithErrors: TemplateFormSectionModel[] = [];
        const sections = props.appliedAppTemplate.templateForm.templateFormSections;

        for (const section of sections) {
            const sectionFieldIds = section.templateFormFields?.map(f => f.id || f.name) || [];
            const hasErrorsInSection = sectionFieldIds.some(fieldId => props.fieldValidationErrors[fieldId] === true);

            if (hasErrorsInSection && section.title) {
                sectionsWithErrors.push(section);
            }
        }

        return sectionsWithErrors;
    };

    const sectionsWithValidationErrors = getSectionsWithValidationErrors();

    // Render clickable section links
    const renderSectionLinks = (sections: TemplateFormSectionModel[], prefix: string) => {
        return (
            <>
                {prefix}{' '}
                {sections.map((section, index) => (
                    <React.Fragment key={section.id || section.name || index}>
                        {props.onSectionSelect ? (
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    props.onSectionSelect(section);
                                }}
                                className="section-link"
                            >
                                {section.title || section.name}
                            </a>
                        ) : (
                            <span>{section.title || section.name}</span>
                        )}
                        {index < sections.length - 1 && ', '}
                    </React.Fragment>
                ))}
            </>
        );
    };

    let submissionState: "none" | "templateConfigDisabled" | "waiting" | "error" | "success" = "none";

    if (props.templateConfiguration?.isDisabled) {
        submissionState = "templateConfigDisabled";
    } else if (isFormSubmitted) {
        if (props.appliedAppTemplateContainer.state.isWaitingForTemplateSync || props.applicationEnvironmentsContainer.state.isCreating)
            submissionState = "waiting";
        else if (props.appliedAppTemplateContainer.state.templateSyncErrors && props.appliedAppTemplateContainer.state.templateSyncErrors.length > 0)
            submissionState = "error";
        else
            submissionState = "success";
    };

    return (
        <>
            {props.appliedAppTemplateContainer.state.isLoading ?
                <Loader />
                :
                <form onSubmit={handleFormSubmit}>
                    <PanelHeaderContainer>
                        <div className={buttonContainerStyle}>
                            {submissionState != "waiting" &&
                                <Button disabled={props.appliedAppTemplateContainer.state.isSaving}
                                    themes={["white-small"]}
                                    text="Previous"
                                    type="button"
                                    onClick={props.onPrevious}
                                />
                            }
                            {submissionState != "success" ?
                                <Button
                                    disabled={hasValidationErrors || props.appliedAppTemplateContainer.state.isSaving || submissionState == "waiting" || incompleteSections.length > 0}
                                    loading={props.appliedAppTemplateContainer.state.isSaving || submissionState == "waiting"}
                                    themes={["primary-small"]}
                                    text={"Finish"}
                                    type="button"
                                    onClick={submitForm}
                                />
                                :
                                <Button
                                    disabled={hasValidationErrors || incompleteSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving}
                                    loading={props.appliedAppTemplateContainer.state.isSaving}
                                    themes={["primary-small"]}
                                    text={"Home"}
                                    type="button"
                                    onClick={goBack}
                                />
                            }
                        </div>
                    </PanelHeaderContainer>
                    <PanelContainer>
                        <CustomScrollbars>
                            <div className={containerStyle}>
                                <h4>Review</h4>
                                {submissionState == "success" &&
                                    <div className="successful-sync">
                                        <img src={successfulSyncIcon} />
                                        Success! Your content has been generated.
                                    </div>
                                }
                                {submissionState == "none" &&
                                    <>
                                        <TemplateReviewSection
                                            title="Status Check"
                                            icon={incompleteSections.length > 0 || hasValidationErrors ? incompleteIcon : completeIcon}
                                            descriptionContent={
                                                hasValidationErrors && sectionsWithValidationErrors.length > 0
                                                    ? renderSectionLinks(sectionsWithValidationErrors, "We can't move forward yet—please go back and fix the highlighted issues in:")
                                                    : hasValidationErrors
                                                        ? "One or more fields aren't in the right format."
                                                        : incompleteSections.length > 0
                                                            ? renderSectionLinks(incompleteSections, "You have incomplete sections:")
                                                            : "It looks like you've filled in all the required fields."
                                            }
                                        />
                                        <TemplateReviewSection
                                            title="Generate Content"
                                            icon={generateIcon}
                                            description={
                                                hasValidationErrors ?
                                                    "Check the sections listed above and correct any fields that aren't in the right format."
                                                    : incompleteSections.length > 0 ?
                                                        "You need to complete all sections before you can generate content."
                                                        : "When you click \"Finish\" your content will be generated based on the values you entered."
                                            }
                                        />
                                        <div className="button-container">
                                            <Button
                                                disabled={hasValidationErrors || incompleteSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving}
                                                loading={props.appliedAppTemplateContainer.state.isSaving}
                                                themes={["primary"]}
                                                text={"Finish"}
                                                type="button"
                                                onClick={submitForm} />
                                        </div>
                                    </>
                                }
                                {submissionState == "success" &&
                                    <div className="button-container">
                                        <Button
                                            disabled={incompleteSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving}
                                            loading={props.appliedAppTemplateContainer.state.isSaving}
                                            themes={["primary"]}
                                            text={"Home"}
                                            type="button"
                                            onClick={goBack}
                                        />
                                    </div>
                                }
                                {submissionState == "waiting" &&
                                    <TemplateReviewSection title="Processing..." icon={processingIcon} description="We’re busy writing out your content. Please wait until this process is complete before you try to initiate another update." />
                                }
                                {submissionState == "templateConfigDisabled" &&
                                    <TemplateReviewSection title="Disabled" icon={errorIcon} description="This module has been disabled and you are unable to use it further." />
                                }
                                {submissionState == "error" &&
                                    <>
                                        <TemplateReviewSection title="Errors" icon={errorIcon} description={`There was a problem syncing your template.\n ${props.appliedAppTemplateContainer.state.templateSyncErrors.toString()}`} />

                                        <div className="button-container">
                                            <Button disabled={incompleteSections.length > 0 || props.appliedAppTemplateContainer.state.isSaving} loading={props.appliedAppTemplateContainer.state.isSaving}
                                                themes={["primary"]}
                                                text={"Retry"}
                                                type="button"
                                                onClick={submitForm} />
                                        </div>
                                    </>
                                }
                            </div>
                        </CustomScrollbars>
                    </PanelContainer>
                </form >
            }
            <TemplateSaveChangesModal
                showSaveChangesModal={showSaveChangesModal}
                closeModal={closeSaveChangesModal}
                changedFields={props.appliedAppTemplateContainer.getChangedFieldsForDisplay(
                    props.appliedAppTemplate?.id || "",
                    props.values.templateFormValues || []
                )}
                submitChanges={performFinish}
                isLoading={props.appliedAppTemplateContainer.state.isSaving}
                hasErrors={false}
                isFinishAction={true}
            />
        </>
    )
}

const buttonContainerStyle = css`
    margin-right: 16px;
    margin-left: auto;
    button {
        margin: 0 16px 0 0;
    }
`;

const containerStyle = css`
    padding: 32px;
    h4 {
        font-style: normal;
        font-weight: normal;
        font-size: 18px;
        line-height: 24px;
        color: ${color_text_default};
    }
    .button-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    .successful-sync {
        margin-top: 16px;
        padding: 16px;
        display: flex;
        background-color: ${color_colors_growth_background};
        border: solid 1px ${color_colors_growth};
        border-radius: 8px;
        img {
            margin-right: 16px;
        }
    }
    .section-link {
        color: ${color_text_link};
        text-decoration: none;
        cursor: pointer;
        font-weight: 600;
        transition: opacity 0.2s ease;
        &:hover {
            opacity: 0.7;
        }
    }
`;

const AppliedAppTemplateReviewForm = withFormik<AppliedAppTemplatesManagerProps, AppliedAppTemplatesManagerFormData>({
    mapPropsToValues: props => (
        {
            templateFormValues: [...props.appliedAppValues],
            isSuccess: false,
            modifiedFields: []
        }
    ),
    validationSchema: yup.object().shape({
    }),
    handleSubmit: async (values, { props }) => {
        const currentAppliedTemplateId = props.appliedAppTemplateId;
        const modifiedFields = (values as any).modifiedFields || [];

        // Use the unified saveTemplateFields method with finalize option
        await props.appliedAppTemplateContainer.saveTemplateFields(
            props.applicationId,
            currentAppliedTemplateId,
            values.templateFormValues,
            {
                finalize: true,
                useModifiedFieldsOnly: modifiedFields.length > 0
            }
        );
    }
})(InnerForm);


export default AppliedAppTemplateReviewForm;